/**
 * PinnedApprovals.jsx - Shows pending approvals as a pinned banner at top of chat
 * Like WhatsApp pinned messages, with inline approve buttons
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Mail, Calendar, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { endpoints } from '../../api/api';

const PinnedApprovals = ({ darkMode, user }) => {
    const [approvals, setApprovals] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch pending approvals
    useEffect(() => {
        const fetchApprovals = async () => {
            if (!user) return;

            try {
                const res = await endpoints.drafts.list(user._id || user.id);
                const pending = (res.data || []).filter(d => d.status === 'pending');
                setApprovals(pending);
            } catch (e) {
                console.error('Error fetching approvals:', e);
            }
        };

        fetchApprovals();
        // Poll every 30 seconds
        const interval = setInterval(fetchApprovals, 30000);
        return () => clearInterval(interval);
    }, [user]);

    const handleApprove = async (draftId) => {
        setLoading(true);
        try {
            await endpoints.drafts.approve(draftId);
            setApprovals(prev => prev.filter(a => a.id !== draftId));
        } catch (e) {
            console.error('Approval failed:', e);
        }
        setLoading(false);
    };

    const handleReject = async (draftId) => {
        try {
            await endpoints.drafts.reject(draftId);
            setApprovals(prev => prev.filter(a => a.id !== draftId));
        } catch (e) {
            console.error('Rejection failed:', e);
        }
    };

    if (approvals.length === 0) return null;

    const displayApprovals = expanded ? approvals : approvals.slice(0, 1);

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mx-4 mt-3 rounded-xl border overflow-hidden ${darkMode
                    ? 'bg-amber-500/10 border-amber-500/20'
                    : 'bg-amber-50 border-amber-200'
                }`}
        >
            {/* Header */}
            <div className={`px-4 py-2 flex items-center justify-between ${darkMode ? 'bg-amber-500/20' : 'bg-amber-100'
                }`}>
                <div className="flex items-center gap-2">
                    <AlertCircle size={16} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                        {approvals.length} Pending Approval{approvals.length > 1 ? 's' : ''}
                    </span>
                </div>
                {approvals.length > 1 && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className={`p-1 rounded hover:bg-black/10 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}
                    >
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                )}
            </div>

            {/* Approval Items */}
            <AnimatePresence>
                {displayApprovals.map((approval, idx) => (
                    <motion.div
                        key={approval.id || idx}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={`px-4 py-3 flex items-center justify-between gap-3 ${idx > 0 ? (darkMode ? 'border-t border-white/5' : 'border-t border-amber-100') : ''
                            }`}
                    >
                        {/* Icon & Info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg ${darkMode ? 'bg-white/10' : 'bg-amber-100'}`}>
                                {approval.type === 'email' ? (
                                    <Mail size={16} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                                ) : (
                                    <Calendar size={16} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                                    {approval.subject || approval.title || 'Draft Email'}
                                </p>
                                <p className={`text-xs truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    To: {Array.isArray(approval.to) ? approval.to.join(', ') : approval.to}
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleReject(approval.id)}
                                className={`p-2 rounded-lg transition-colors ${darkMode
                                        ? 'hover:bg-red-500/20 text-red-400'
                                        : 'hover:bg-red-100 text-red-500'
                                    }`}
                                title="Reject"
                            >
                                <X size={16} />
                            </button>
                            <button
                                onClick={() => handleApprove(approval.id)}
                                disabled={loading}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${darkMode
                                        ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                                        : 'bg-green-500 hover:bg-green-600 text-white'
                                    }`}
                            >
                                <Check size={14} />
                                Approve
                            </button>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </motion.div>
    );
};

export default PinnedApprovals;
